import {Group, HoverCard} from '@mantine/core'
import {format} from 'date-fns'
import type {MouseEvent} from 'react'
import {useMemo} from 'react'

import {AssignmentStrip} from 'src/assignments/assignment-strip'
import {
    type AssignmentStripDateRangeOptions,
    type AssignmentStripDay,
    type AssignmentStripDayStatus,
    buildAssignmentStripDays,
} from 'src/assignments/assignment-strip-utils'

import classes from './dispatch-assignment-panel.module.css'
import {
    boardAssignmentsForResourceStrip,
    DISPATCH_TYPEAHEAD_STRIP_DATE_RANGE,
} from './dispatch-board-resource-strip'
import type {DispatchTimelineData} from './types'

export type BusyDayHighlightRange = {
    startDate: string
    endDate: string | null
}

type DispatchResourceWeekStripProps = {
    resourceId: string
    displayData: DispatchTimelineData
    referenceDate: Date
    resourceKind?: 'worker' | 'rental_asset'
    /** Job open in the assignment modal — omitted from busy so self-assign isn't a false conflict. */
    excludeJobId?: string
    compact?: boolean
    /**
     * When set, busy days that fall in this range use attention orange (Conflict
     * overlap with the job being assigned). Other busy days stay availability gray.
     */
    conflictHighlightRange?: BusyDayHighlightRange | null
    /** Date range rendered as job days. Defaults to the reference day. */
    jobDays?: BusyDayHighlightRange | null
    /** Open another board job from a sparkline day (replaces the current drawer). */
    onOpenBoardJob?: (jobId: string) => void
}

const STATUS_LABEL: Record<AssignmentStripDayStatus, string> = {
    unassigned: 'Available',
    bounded: 'Assigned',
    open_ended: 'Assigned',
}

function dateKeyInRange(dateKey: string, range: BusyDayHighlightRange): boolean {
    if (dateKey < range.startDate) {
        return false
    }
    if (range.endDate == null) {
        return true
    }
    return dateKey <= range.endDate
}

function stopComboboxSelect(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
}

function CompactDayBookings({
    day,
    isConflictDay,
    onOpenBoardJob,
}: {
    day: AssignmentStripDay
    isConflictDay: boolean
    onOpenBoardJob?: (jobId: string) => void
}) {
    if (day.assignments.length === 0) {
        return (
            <div className={classes.sparklineTip}>
                <p className={classes.sparklineTipEmpty}>Available</p>
                <p className={classes.sparklineTipDate}>{format(day.date, 'EEE MMM d')}</p>
            </div>
        )
    }

    return (
        <div className={classes.sparklineTip}>
            <ul className={classes.sparklineTipList}>
                {day.assignments.map((assignment) => (
                    <li key={assignment.id} className={classes.sparklineTipItem}>
                        <p className={classes.sparklineTipJob}>{assignment.jobName}</p>
                        <div className={classes.sparklineTipMeta}>
                            <p className={classes.sparklineTipDate}>
                                {format(day.date, 'EEE MMM d')}
                            </p>
                            {isConflictDay ? (
                                <span className={classes.sparklineTipBadge}>Conflict</span>
                            ) : (
                                <span className={classes.sparklineTipMutedBadge}>Booked</span>
                            )}
                        </div>
                        {onOpenBoardJob ? (
                            <button
                                type="button"
                                className={classes.sparklineTipAction}
                                data-testid={`sparkline-open-job-${assignment.jobId}`}
                                onMouseDown={stopComboboxSelect}
                                onClick={(event) => {
                                    stopComboboxSelect(event)
                                    onOpenBoardJob(assignment.jobId)
                                }}
                            >
                                Open on board
                                <span aria-hidden>→</span>
                            </button>
                        ) : null}
                    </li>
                ))}
            </ul>
        </div>
    )
}

function CompactWeekDay({
    day,
    conflictHighlightRange,
    jobDays,
    onOpenBoardJob,
}: {
    day: AssignmentStripDay
    conflictHighlightRange?: BusyDayHighlightRange | null
    jobDays?: BusyDayHighlightRange | null
    onOpenBoardJob?: (jobId: string) => void
}) {
    const dayLabel = format(day.date, 'EEEEE')
    const isJobDay = jobDays != null ? dateKeyInRange(day.dateKey, jobDays) : day.isReferenceDay
    const isBusy = day.status === 'bounded' || day.status === 'open_ended'
    const isConflictDay =
        isBusy &&
        conflictHighlightRange != null &&
        dateKeyInRange(day.dateKey, conflictHighlightRange)

    const dot = (
        <button
            type="button"
            className={classes.compactWeekDot}
            data-status={day.status}
            data-reference={isJobDay || undefined}
            data-conflict-day={isConflictDay || undefined}
            data-testid={`sparkline-day-${day.dateKey}`}
            aria-label={`${format(day.date, 'EEE MMM d')}${isJobDay ? ' (job day)' : ''}: ${STATUS_LABEL[day.status]}${isConflictDay ? ' (conflicts with this job)' : ''}`}
            onMouseDown={stopComboboxSelect}
            onClick={(event) => {
                stopComboboxSelect(event)
                if (!onOpenBoardJob || day.assignments.length === 0) {
                    return
                }
                // Conflict days: jump to the overlapping booking; other busy days open first booking.
                onOpenBoardJob(day.assignments[0]!.jobId)
            }}
        >
            {dayLabel}
        </button>
    )

    if (!isBusy && day.assignments.length === 0) {
        return dot
    }

    return (
        <HoverCard
            width={248}
            shadow="md"
            withArrow
            openDelay={100}
            closeDelay={80}
            withinPortal
            position="bottom"
            offset={8}
            classNames={{dropdown: classes.sparklineTipDropdown}}
        >
            <HoverCard.Target>{dot}</HoverCard.Target>
            <HoverCard.Dropdown p={0} onMouseDown={stopComboboxSelect}>
                <CompactDayBookings
                    day={day}
                    isConflictDay={isConflictDay}
                    onOpenBoardJob={onOpenBoardJob}
                />
            </HoverCard.Dropdown>
        </HoverCard>
    )
}

function DispatchCompactWeekStrip({
    assignments,
    referenceDate,
    dateRange,
    conflictHighlightRange = null,
    jobDays = null,
    onOpenBoardJob,
}: {
    assignments: ReturnType<typeof boardAssignmentsForResourceStrip>
    referenceDate: Date
    dateRange: AssignmentStripDateRangeOptions
    conflictHighlightRange?: BusyDayHighlightRange | null
    jobDays?: BusyDayHighlightRange | null
    onOpenBoardJob?: (jobId: string) => void
}) {
    const days = buildAssignmentStripDays(assignments, referenceDate, dateRange)

    return (
        <Group
            gap={3}
            wrap="nowrap"
            className={classes.compactWeekDays}
            aria-label="Week availability"
        >
            {days.map((day) => (
                <CompactWeekDay
                    key={day.dateKey}
                    day={day}
                    conflictHighlightRange={conflictHighlightRange}
                    jobDays={jobDays}
                    onOpenBoardJob={onOpenBoardJob}
                />
            ))}
        </Group>
    )
}

/**
 * Week availability for the dispatch picker — always derived from the loaded board
 * so assign/unassign updates immediately. Job-detail rental-asset assignment APIs
 * use open-ended schedules that falsely paint the rest of the week busy.
 */
export function DispatchResourceWeekStrip({
    resourceId,
    displayData,
    referenceDate,
    excludeJobId,
    compact = false,
    conflictHighlightRange = null,
    jobDays = null,
    onOpenBoardJob,
}: DispatchResourceWeekStripProps) {
    const stripDateRange = DISPATCH_TYPEAHEAD_STRIP_DATE_RANGE

    const assignments = useMemo(() => {
        const rows = boardAssignmentsForResourceStrip(displayData, resourceId)
        if (excludeJobId == null) {
            return rows
        }
        return rows.filter((row) => row.jobId !== excludeJobId)
    }, [displayData, excludeJobId, resourceId])

    if (compact) {
        return (
            <DispatchCompactWeekStrip
                assignments={assignments}
                referenceDate={referenceDate}
                dateRange={stripDateRange}
                conflictHighlightRange={conflictHighlightRange}
                jobDays={jobDays}
                onOpenBoardJob={onOpenBoardJob}
            />
        )
    }

    return (
        <div className={classes.weekStrip}>
            <AssignmentStrip
                assignments={assignments}
                referenceDate={referenceDate}
                referenceDayLabel="Job day"
                dayInteraction="none"
                dateRange={stripDateRange}
                presentation="availability"
                title="Week availability"
                showLegend={false}
            />
        </div>
    )
}
